import { useEffect, useState } from 'react';
import {
  FORMAT,
  formatDate,
  getCurrentWeekDates,
  getCalendarDates,
} from '../../../lib/date';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar as BarChart } from 'react-chartjs-2';
import RingProgress from '../../../components/progress/ring';
import Bar from '../../../components/progress/bar';
import request from '../../../lib/request';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const { dataset, formattedValue, raw } = context;
          if (dataset.label === 'Calories') {
            return `${dataset.label}: ${formattedValue} Cal`;
          } else {
            if (dataset.label === 'Protein') {
              return `${dataset.label}: ${(raw / 4).toFixed(1)} g`;
            } else if (dataset.label === 'Carbs') {
              return `${dataset.label}: ${(raw / 4).toFixed(1)} g`;
            } else if (dataset.label === 'Fats') {
              return `${dataset.label}: ${(raw / 9).toFixed(1)} g`;
            }
          }
        },
      },
    },
  },
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
    },
  },
};

const calculatePercentage = (value, total) => {
  return Math.floor((value / total) * 100);
};

const calculateDailyMacronutrients = (ingredients) => {
  if (!ingredients) {
    return { protein: 0, carbs: 0, fat: 0 };
  }

  const protein = ingredients.reduce(
    (acc, cur) => acc + (cur.proteins ?? 0),
    0
  );
  const carbs = ingredients.reduce(
    (acc, cur) => acc + (cur.carbohydrates ?? 0),
    0
  );
  const fat = ingredients.reduce((acc, cur) => acc + (cur.fats ?? 0), 0);
  return { protein, carbs, fat };
};

const MealStats = ({ todayMeals }) => {
  const [goal, setGoal] = useState({});
  const [loading, setLoading] = useState(false);
  const [dailyTotalCalorie, setDailyTotalCalorie] = useState(0);
  const [dailyProtein, setDailyProtein] = useState(0);
  const [dailyCarbs, setDailyCarbs] = useState(0);
  const [dailyFat, setDailyFat] = useState(0);
  const [statView, setStatView] = useState('TODAY');
  const [mealList, setMealList] = useState([]);
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    getUserGoal();
    getTodayTotalCalories();
    setStatView('TODAY');
  }, [todayMeals]);

  const getUserGoal = async () => {
    try {
      setLoading(true);
      const { result } = await request.get('/user/goal');
      if (result?.length) {
        setGoal(result[0]);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getTodayTotalCalories = () => {
    if (todayMeals?.list) {
      setDailyTotalCalorie(todayMeals.totalCalories);

      let protein = 0;
      let carbs = 0;
      let fat = 0;

      for (const item of todayMeals.list) {
        const {
          protein: itemProtein,
          carbs: itemCarbs,
          fat: itemFat,
        } = calculateDailyMacronutrients(item.ingredients);
        protein += itemProtein;
        carbs += itemCarbs;
        fat += itemFat;
      }

      setDailyProtein(protein.toFixed(1));
      setDailyCarbs(carbs.toFixed(1));
      setDailyFat(fat.toFixed(1));
    }
  };

  const getMealsBetween = async (startDate, endDate) => {
    try {
      const { result } = await request.get(
        `/meal?startDate=${startDate}&endDate=${endDate}`
      );
      if (result?.length > 0) {
        setMealList(result);
      } else {
        setMealList([]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const changeStatView = (view) => {
    setStatView(view);
    if (view === 'TODAY') {
      setStatView('TODAY');
    } else if (view === 'WEEK') {
      setStatView('WEEK');
      const currentWeekDates = getCurrentWeekDates();
      setDateRange(
        currentWeekDates.map((date) => formatDate(new Date(date), FORMAT.DATE))
      );
      getMealsBetween(currentWeekDates[0], currentWeekDates[6]);
    } else if (view === 'MONTH') {
      setStatView('MONTH');
      const year = new Date().getFullYear();
      const month = new Date().getMonth() + 1;
      const lastDay = new Date(year, month, 0).getDate();

      const monthObj = getCalendarDates(year, month);
      setDateRange(
        monthObj[String(year)][String(month).padStart(2, '0')].map(
          (date) => `${year}-${String(month).padStart(2, '0')}-${date}`
        )
      );

      getMealsBetween(
        `${year}-${String(month).padStart(2, '0')}-01`,
        `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(
          2,
          '0'
        )}`
      );
    }
  };

  return (
    <div>
      {loading ? (
        <div className='mb-2 rounded-xl px-3 py-2 bg-secondary min-h-52 flex items-center justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-t-2 border-white'></div>
        </div>
      ) : (
        <div className='mb-2 rounded-xl p-2 bg-secondary'>
          <div className='grid grid-cols-3 text-sm relative py-1 mb-2'>
            <div
              className='text-white text-center cursor-pointer z-10'
              onClick={() => changeStatView('TODAY')}
            >
              Today
            </div>
            <div
              className='text-white text-center cursor-pointer z-10'
              onClick={() => changeStatView('WEEK')}
            >
              This Week
            </div>
            <div
              className='text-white text-center cursor-pointer z-10'
              onClick={() => changeStatView('MONTH')}
            >
              This Month
            </div>
            <div
              className='absolute top-0 left-0 h-full bg-tertiary w-1/3 rounded-full transition-transform'
              style={{
                transform: `translateX(${
                  statView === 'TODAY' ? 0 : statView === 'WEEK' ? 100 : 200
                }%)`,
              }}
            ></div>
          </div>
          {statView === 'TODAY' && (
            <div className='flex items-center justify-evenly'>
              <RingProgress
                percentage={calculatePercentage(
                  dailyTotalCalorie,
                  goal.dailyCalorie ?? 0
                )}
                color='#F8A171'
                text={`${dailyTotalCalorie} / ${goal.dailyCalorie ?? 0} Cal`}
                size={142}
              />
              <div className='flex flex-col gap-2 w-[140px] pr-2'>
                <Bar
                  title='Protein'
                  percentage={calculatePercentage(
                    Number(dailyProtein),
                    Number(goal.dailyProtein ?? 0)
                  )}
                  current={Number(dailyProtein)}
                  total={Number(goal.dailyProtein ?? 0)}
                  color='#FF6B6B'
                  unit='g'
                />
                <Bar
                  title='Carbs'
                  percentage={calculatePercentage(
                    Number(dailyCarbs),
                    Number(goal.dailyCarbs ?? 0)
                  )}
                  current={Number(dailyCarbs)}
                  total={Number(goal.dailyCarbs ?? 0)}
                  color='#4ECDC4'
                  unit='g'
                />
                <Bar
                  title='Fats'
                  percentage={calculatePercentage(
                    Number(dailyFat),
                    Number(goal.dailyFat ?? 0)
                  )}
                  current={Number(dailyFat)}
                  total={Number(goal.dailyFat ?? 0)}
                  color='#FFD93D'
                  unit='g'
                />
              </div>
            </div>
          )}
          {statView === 'WEEK' && (
            <div className='flex items-center justify-center'>
              <BarChart
                data={{
                  labels: dateRange.map((date) => date.slice(5)),
                  datasets: [
                    {
                      label: 'Calories',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) => acc + meal.calorie,
                          0
                        );
                      }),
                      backgroundColor: '#F8A171',
                    },
                    {
                      label: 'Fats',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) =>
                            acc +
                            calculateDailyMacronutrients(meal.ingredients).fat *
                              9,
                          0
                        );
                      }),
                      backgroundColor: '#FFD93D',
                      stack: 'macronutrients',
                    },
                    {
                      label: 'Carbs',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) =>
                            acc +
                            calculateDailyMacronutrients(meal.ingredients)
                              .carbs *
                              4,
                          0
                        );
                      }),
                      backgroundColor: '#4ECDC4',
                      stack: 'macronutrients',
                    },
                    {
                      label: 'Protein',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) =>
                            acc +
                            calculateDailyMacronutrients(meal.ingredients)
                              .protein *
                              4,
                          0
                        );
                      }),
                      backgroundColor: '#FF6B6B',
                      stack: 'macronutrients',
                    },
                  ],
                }}
                options={options}
              />
            </div>
          )}
          {statView === 'MONTH' && (
            <div className='flex items-center justify-center'>
              <BarChart
                data={{
                  labels: dateRange.map((date) => date.slice(5)),
                  datasets: [
                    {
                      label: 'Calories',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) => acc + meal.calorie,
                          0
                        );
                      }),
                      backgroundColor: '#F8A171',
                    },
                    {
                      label: 'Fats',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) =>
                            acc +
                            calculateDailyMacronutrients(meal.ingredients).fat *
                              9,
                          0
                        );
                      }),
                      backgroundColor: '#FFD93D',
                      stack: 'macronutrients',
                    },
                    {
                      label: 'Carbs',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) =>
                            acc +
                            calculateDailyMacronutrients(meal.ingredients)
                              .carbs *
                              4,
                          0
                        );
                      }),
                      backgroundColor: '#4ECDC4',
                      stack: 'macronutrients',
                    },
                    {
                      label: 'Protein',
                      data: dateRange.map((date) => {
                        const list = mealList.filter(
                          (meal) => meal.logDate === date
                        );
                        return list.reduce(
                          (acc, meal) =>
                            acc +
                            calculateDailyMacronutrients(meal.ingredients)
                              .protein *
                              4,
                          0
                        );
                      }),
                      backgroundColor: '#FF6B6B',
                      stack: 'macronutrients',
                    },
                  ],
                }}
                options={options}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MealStats;
